import { Checkbox, Collapse, Spin } from "antd"
import { ReactNode } from "react"

// ─── Types ───────────────────────────────────────────────────────────────────

interface CollapseTableHeader {
  label: string
  width?: string
}

interface Department {
  id: number | string
  name: string
}

interface GroupedDepartment<T> {
  department: Department
  employees: T[]
}

interface CollapseTableProps<T> {
  headers: CollapseTableHeader[]
  departments: GroupedDepartment<T>[]
  activeCollapseKeys: string | string[] | undefined
  isLoading: boolean
  emptyMessage?: string
  selectedRow: (string | number)[]
  onCollapseChange: (key: string) => void
  onSelectAll: (employees: T[]) => void
  renderRow: (employee: T) => ReactNode
  getEmployeeId: (employee: T) => number
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const isActiveKey = (
  activeCollapseKeys: string | string[] | undefined,
  id: string | number
) => String(activeCollapseKeys) === String(id)

// ─── Main Component ───────────────────────────────────────────────────────────

function TimeSheetTable<T>({
  headers,
  departments,
  activeCollapseKeys,
  isLoading,
  emptyMessage = "No data found",
  selectedRow,
  onCollapseChange,
  onSelectAll,
  renderRow,
  getEmployeeId,
}: CollapseTableProps<T>) {
  const gridCols = headers.map((h) => h.width || "1fr").join(" ")

  return (
    <>
      {departments.length ? (
        <div className="border border-slate-300 rounded-t-lg overflow-hidden">
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {/* Table Header */}
              <ul
                className="grid text-slate-500 items-center bg-slate-50 border-b border-slate-300 font-medium py-3 px-4"
                style={{ gridTemplateColumns: gridCols }}
              >
                {headers.map(({ label }) => (
                  <li key={label}>{label}</li>
                ))}
              </ul>

              {/* Collapse */}
              <Collapse
                style={{ background: "transparent" }}
                className="[&_.ant-collapse-item]:py-2 [&_.ant-collapse-content-box]:!p-0 [&_.ant-collapse-header]:!pl-0"
                bordered={false}
                accordion
                activeKey={activeCollapseKeys}
                expandIcon={() => null}
                onChange={(keys) => onCollapseChange(keys.toString())}
                items={departments?.map((item) => {
                  const isActive = isActiveKey(
                    activeCollapseKeys,
                    item.department.id
                  )
                  const employees = item.employees
                  const empIds = employees.map(getEmployeeId)
                  const total = empIds.length
                  const selectedCount = empIds.filter((id) =>
                    selectedRow.includes(id)
                  ).length
                  const isAllSelected = total > 0 && selectedCount === total

                  return {
                    key: item.department.id,
                    label: (
                      <CollapseLabel
                        department={item.department}
                        isActive={isActive}
                        isAllSelected={isAllSelected}
                        onSelectAll={() => onSelectAll(employees)}
                      />
                    ),
                    children: (
                      <CollapseContent
                        isLoading={isLoading}
                        employees={employees}
                        emptyMessage={emptyMessage}
                        renderRow={renderRow}
                      />
                    ),
                  }
                })}
              />
            </div>
          </div>
        </div>
      ) : (
        <img
          src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/53252fa3-8e1d-47fa-969d-4816aefc1993.png"
          className="object-contain"
        />
      )}
    </>
  )
}

// ─── Sub Components ───────────────────────────────────────────────────────────

interface CollapseLabelProps {
  department: Department
  isActive: boolean
  isAllSelected: boolean
  onSelectAll: () => void
}

export const CollapseLabel = ({
  department,
  isActive,
  isAllSelected,
  onSelectAll,
}: CollapseLabelProps) => (
  <div className="flex justify-between items-center w-full">
    <span className="flex items-center gap-2 sticky left-0 bg-white pl-4">
      <Checkbox
        onChange={onSelectAll}
        checked={isAllSelected}
      />
      <p className="font-medium capitalize">{department.name}</p>
    </span>
    <button className="flex items-center gap-2 text-sm text-slate-500 sticky right-0 bg-white px-2">
      <span>{isActive ? "Collapse" : "Expand"}</span>
      {isActive ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m4.5 15.75 7.5-7.5 7.5 7.5"
          />
        </svg>
      ) : (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-4 h-4"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="m19.5 8.25-7.5 7.5-7.5-7.5"
          />
        </svg>
      )}
    </button>
  </div>
)

interface CollapseContentProps<T> {
  isLoading: boolean
  emptyMessage: string
  employees: T[]
  renderRow: (employee: T) => ReactNode
}

function CollapseContent<T>({
  isLoading,
  emptyMessage,
  employees,
  renderRow,
}: CollapseContentProps<T>) {
  if (isLoading) {
    return (
      <Spin>
        <div className="h-[94px] w-full bg-white rounded-lg border" />
      </Spin>
    )
  }

  if (!employees?.length) {
    return (
      <p className="py-5 flex justify-center items-center">{emptyMessage}</p>
    )
  }

  return (
    <div className="border-t border-t-gray-300 px-4 [&>*:last-child]:border-b-0">
      {employees.map(renderRow)}
    </div>
  )
}

export default TimeSheetTable
